
import { Modal, TabManager, Toggle } from '05-ui-kit'
import QrcodeVue from 'qrcode.vue'
import { defineComponent } from '@nuxtjs/composition-api'
import { mapActions } from 'pinia'
import ErrorSerializer from '~/serializer/Error'
import { useSocialShareStore } from '~/store/modal/socialShare'
import type { Calculate, InstallmentParams } from '~/type/Installment'

export default defineComponent({
  components: {
    Modal,
    QrcodeVue,
    TabManager,
    Toggle,
  },
  inject: ['emitter'],
  async asyncData(context) {
    try {
      const params = {
        price: Number(context.query.price) || 70000,
        period: Number(context.query.period) || 3,
        first_fee_in_rouble: Number(context.query.first_fee_in_rouble) || 60000,
      }

      const response = (await context.$api.installment.calculate.call(context, { ...params }))

      return {
        initialValue: response.result,
        params,
      }
    }
    catch (e) {
      const error = ErrorSerializer(e)
      new context.$pageError(error)
    }
  },
  data(): {
    qrCodeActive: boolean
    urlPage: string
    pageCategoryID: number
    pageCategory: { id: number; name: string }[]
    faq: { [key: string]: { question: string; answer: string }[] }
    advantages: {
      title: string
      description: string
      key: string
      bgColor: string
      link?: string
    }[]
    params?: InstallmentParams
    initialValue?: Calculate
  } {
    return {
      initialValue: undefined,
      qrCodeActive: false,
      urlPage: '',
      pageCategoryID: 1,
      advantages: [
        {
          title: 'По нормам ислама',
          description: 'Соответствие принципам шариата. Прозрачные условия',
          key: 'islam',
          bgColor: '#E9F9EF',
        },
        {
          title: 'Без скрытых платежей',
          description: 'Сколько заплатите известно сразу',
          key: 'pay',
          bgColor: '#FFEEED',
        },
        {
          title: 'Промо акции',
          description: 'Специальные предложения с выгодой для вас',
          key: 'sale',
          bgColor: '#FAFAFA',
        },
        {
          title: 'Личный кабинет',
          description: 'Удобное управление рассрочкой онлайн',
          key: 'lk',
          bgColor: '#FEF8EC',
          link: '/user/?bnpl=1',
        },
        {
          title: 'Без посредников',
          description: 'Напрямую от нашего магазина, без лишних переплат',
          key: 'intermediary',
          bgColor: '#FEF8EC',
        },
      ],
      pageCategory: [
        {
          id: 0,
          name: 'Условия и расчет',
        },
        {
          id: 1,
          name: 'Частые вопросы',
        },
        {
          id: 2,
          name: 'Юридическая информация',
        },
      ],
      faq: {
        registration: [
          {
            question: 'Какие документы нужны для того, чтобы оформить рассрочку?',
            answer: 'Для оформления рассрочки на сайте или в магазине нужен только паспорт',
          },
          {
            question: 'Как я могу оформить рассрочку?',
            answer: 'Есть 2 способа. Первый — это прийти в любой магазин 05.ru в Махачкале, Дербенте, Избербаше или Хасавюрте. Второй — оформить рассрочку в нашем интернет-магазине',
          },
          {
            question: 'Какие у вас требования к прописке?',
            answer: 'Прописка любого региона России, обязательное требование фактическое проживание и работа в Республике Дагестан.',
          },
          {
            question: 'Нужно предоставлять справку о доходах?',
            answer: 'Нет',
          },
          {
            question: 'А есть ограничения по возрасту?',
            answer: 'Да: возраст от 23 до 75 лет',
          },
          {
            question: 'Могу ли я оформить еще одну рассрочку?',
            answer: 'Да, если сделано хотя бы 3 платежа по действующей рассрочке;',
          },
          {
            question: 'Есть способ повысить вероятность одобрения рассрочки?',
            answer: 'Да, при предоставлении 2-х поручителей.',
          },
          {
            question: 'Должен ли поручитель приходить в магазин?',
            answer: 'Нет, для поручительства будет достаточно телефонного звонка',
          },
          {
            question: 'В чем принципиальное отличие рассрочки от кредита?',
            answer: 'Рассрочка — это договор купли-продажи товара. Торговля в Исламе разрешена, как и разрешена торговля с отсрочкой платежа. При этом продавец имеет право увеличить цену, при продаже товара с отсрочкой платежа. В договоре купли-продажи с отсрочкой платежа стоимость товара зафиксирована и не может измениться ни при каких обстоятельствах. Кредит — это ссуда денег под проценты со всеми вытекающими: пени, штрафы и так далее. ',
          },
        ],
        products: [
          {
            question: 'Какие товары я могу купить в рассрочку?',
            answer: 'Рассрочка распространяется на любой товар, который представлен в магазине 05.ru',
          },
          {
            question: 'Есть ограничения по стоимости товара?',
            answer: 'Нет, купить в рассрочку можно любой товар. Но сумма рассрочки не должна быть больше 200 000 ₽ после внесения первоначального взноса. Пример: товар стоит 300 000 ₽ с учетом наценки. Чтобы купить его в рассрочку, первоначальный взнос должен быть не меньше 100 000 ₽ — так сумма рассрочки не будет превышать 200 000 ₽',
          },
        ],
        payments: [
          {
            question: 'Какие способы оплаты вы принимаете?',
            answer: 'Наличными на кассе в любом магазине 05.ru',
          },
          {
            question: 'Начисляются ли бонусы при покупке товара в рассрочку?',
            answer: 'Да, если они предусмотрены',
          },
          {
            question: 'А можно оплатить часть стоимости товара бонусами?',
            answer: 'Нет, бонусами нельзя воспользоваться при покупке товара в рассрочку',
          },
          {
            question: 'Как работают акции «БЕЗ ПЕРЕПЛАТ» 35-0-3, 30-0-3, 40-0-4, 50-0-5?',
            answer: `Разберем на примере акции 30-0-3, где 30% — это первоначальный взнос, 0% — торговая наценка, а 3 — срок рассрочки. Товар, на который распространяется эта акция, можно будет купить в рассрочку без переплат на 3 месяца с первоначальным взносом 30%.
      Клиенту товар обойдется ровно в ту же цену, какая указана на витрине при продаже за наличные.
      Уточняйте у менеджеров по продажам и на сайте действует ли ПРОМО «БЕЗ ПЕРЕПЛАТ» на приобретаемый товар.`,
          },
        ],
      },
    }
  },
  head: {
    title: 'Рассрочка от 05RU',
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: 'Оформление рассрочки в 05.ru.',
      },
    ],
  },
  methods: {
    ...mapActions(useSocialShareStore, { enableSocialShare: 'enableSocialShare' }),
    changeParams(data: { type: 'price' | 'firstfee' | 'period'; value: any }) {
      this.$router.push({
        query: { ...this.params, ...data.value },
      })
      this.params = { ...data.value }
      this.$emitter.emit('rassrochka-page', { type: data.type })
    },
    enableShare() {
      this.enableSocialShare({
        path: this.$route.fullPath,
      })
      this.$emitter.emit('rassrochka-page', { type: 'share' })
    },
    enableQrCode() {
      this.urlPage = window.location.href
      this.qrCodeActive = true
      this.$emitter.emit('rassrochka-page', { type: 'qrshare' })
    },
    disabledModalQrCode() {
      this.urlPage = ''
      this.qrCodeActive = false
    },
  },
})
